import React, {
  ChangeEvent,
  DragEvent,
  FormEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import { BASE_URL } from "../../config";
import {
  createGalleryCategory,
  deleteGalleryItem,
  getGallery,
  getGalleryCategories,
  reorderGallery,
  updateGalleryItem,
  uploadGalleryItem,
} from "../../services/gallery";
import type { GalleryCategory, GalleryItem } from "../../types/Gallery";

const initialUpload = {
  categoryId: "",
  newCategory: "",
  caption: "",
  orderNumber: "",
};

const acceptedMediaTypes = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
  "video/mp4",
  "video/webm",
  "video/quicktime",
];

const formatFileSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const isHeicFile = (file: File) =>
  ["image/heic", "image/heif"].includes(file.type) ||
  /\.(heic|heif)$/i.test(file.name);

export default function Gallery() {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [categories, setCategories] = useState<GalleryCategory[]>([]);
  const [upload, setUpload] = useState(initialUpload);
  const [media, setMedia] = useState<File | null>(null);
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<"all" | number>("all");
  const [editingItem, setEditingItem] = useState<GalleryItem | null>(null);
  const [editDraft, setEditDraft] = useState({
    categoryId: "",
    caption: "",
    orderNumber: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadDragging, setIsUploadDragging] = useState(false);
  const [isConvertingHeic, setIsConvertingHeic] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const filterCategories = useMemo(
    () => categories.filter((category) => category.slug !== "all"),
    [categories]
  );
  const filteredItems = useMemo(
    () =>
      activeCategory === "all"
        ? items
        : items.filter((item) => Number(item.category_id) === activeCategory),
    [activeCategory, items]
  );
  const mediaPreviewUrl = useMemo(
    () => (media ? URL.createObjectURL(media) : ""),
    [media]
  );

  const loadData = async () => {
    setIsLoading(true);
    setError("");
    try {
      const [galleryResponse, categoryResponse] = await Promise.all([
        getGallery(),
        getGalleryCategories(),
      ]);
      setItems(galleryResponse.items);
      setCategories(categoryResponse.categories);
      setUpload((current) => ({
        ...current,
        categoryId:
          current.categoryId || String(categoryResponse.categories[0]?.id || ""),
      }));
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to load gallery");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(
    () => () => {
      if (mediaPreviewUrl) URL.revokeObjectURL(mediaPreviewUrl);
    },
    [mediaPreviewUrl]
  );

  useEffect(() => {
    if (!editingItem) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isSaving) setEditingItem(null);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [editingItem, isSaving]);

  const selectMedia = async (file?: File) => {
    setError("");
    if (!file) return;
    if (!acceptedMediaTypes.includes(file.type) && !isHeicFile(file)) {
      setError("Choose a JPG, PNG, WebP, GIF, HEIC, MP4, WebM, or MOV file.");
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      setError("The selected file is larger than the 200 MB limit.");
      return;
    }

    if (!isHeicFile(file)) {
      setMedia(file);
      return;
    }

    setMedia(null);
    setIsConvertingHeic(true);
    try {
      const { default: heic2any } = await import("heic2any");
      const converted = await heic2any({
        blob: file,
        toType: "image/jpeg",
        quality: 0.84,
      });
      const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
      if (!jpegBlob) throw new Error("The HEIC file did not contain an image.");
      const jpegName = file.name.replace(/\.(heic|heif)$/i, "") + ".jpg";
      setMedia(
        new File([jpegBlob], jpegName, {
          type: "image/jpeg",
          lastModified: file.lastModified,
        })
      );
      setMessage("HEIC converted to JPEG and ready to upload.");
    } catch (conversionError) {
      setError(
        conversionError instanceof Error
          ? `HEIC conversion failed: ${conversionError.message}`
          : "HEIC conversion failed. Please convert the file to JPEG and try again."
      );
    } finally {
      setIsConvertingHeic(false);
    }
  };

  const handleUpload = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setMessage("");
    if (!media) {
      setError("Choose an image or video first.");
      return;
    }

    setIsSaving(true);
    try {
      let categoryId = Number(upload.categoryId);
      if (upload.newCategory.trim()) {
        const response = await createGalleryCategory(upload.newCategory.trim());
        categoryId = response.category.id;
      }
      if (!categoryId) {
        throw new Error("Select a category or create a new one.");
      }

      const form = new FormData();
      form.append("media", media);
      form.append("category_id", String(categoryId));
      form.append("caption", upload.caption);
      if (upload.orderNumber) form.append("order_number", upload.orderNumber);
      const response = await uploadGalleryItem(form);
      setUpload({ ...initialUpload, categoryId: String(categoryId) });
      setMedia(null);
      const input = document.getElementById("gallery-media") as HTMLInputElement | null;
      if (input) input.value = "";
      const processingNote =
        response.processing_ms > 0
          ? ` Converted in ${(response.processing_ms / 1000).toFixed(1)}s.`
          : "";
      setMessage(`Media uploaded and added to the gallery.${processingNote}`);
      await loadData();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Upload failed");
    } finally {
      setIsSaving(false);
    }
  };

  const openEditModal = (item: GalleryItem) => {
    setEditingItem(item);
    setEditDraft({
      categoryId: String(item.category_id),
      caption: item.caption || "",
      orderNumber: String(item.order_number),
    });
    setError("");
  };

  const closeEditModal = () => {
    if (!isSaving) setEditingItem(null);
  };

  const saveItem = async () => {
    if (!editingItem) return;
    setError("");
    setIsSaving(true);
    try {
      const categoryId = Number(editDraft.categoryId);
      const orderNumber = Math.max(0, Number(editDraft.orderNumber) || 0);
      await updateGalleryItem(editingItem.id, {
        category_id: categoryId,
        caption: editDraft.caption,
        order_number: orderNumber,
      });
      const category = categories.find(({ id }) => Number(id) === categoryId);
      setItems((current) =>
        current.map((item) =>
          item.id === editingItem.id
            ? {
                ...item,
                category_id: categoryId,
                category_name: category?.name || item.category_name,
                category_slug: category?.slug || item.category_slug,
                caption: editDraft.caption,
                order_number: orderNumber,
              }
            : item
        )
      );
      setEditingItem(null);
      setMessage("Gallery item updated.");
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Update failed");
    } finally {
      setIsSaving(false);
    }
  };

  const removeItem = async (item: GalleryItem) => {
    if (!window.confirm(`Delete “${item.original_filename}”? This cannot be undone.`)) return;
    setError("");
    try {
      await deleteGalleryItem(item.id);
      setItems((current) => current.filter(({ id }) => id !== item.id));
      if (editingItem?.id === item.id) setEditingItem(null);
      setMessage("Gallery item deleted.");
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Delete failed");
    }
  };

  const startDragging = (event: DragEvent, itemId: number) => {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(itemId));
    setDraggedId(itemId);
    setMessage("");
    setError("");
  };

  const finishDragging = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const dropItem = async (event: DragEvent, targetId: number) => {
    event.preventDefault();
    const sourceId = draggedId || Number(event.dataTransfer.getData("text/plain"));
    finishDragging();
    if (!sourceId || sourceId === targetId) return;

    const previousItems = [...items];
    const reorderedVisible = [...filteredItems];
    const fromIndex = reorderedVisible.findIndex(({ id }) => id === sourceId);
    const toIndex = reorderedVisible.findIndex(({ id }) => id === targetId);
    if (fromIndex < 0 || toIndex < 0) return;

    const [moved] = reorderedVisible.splice(fromIndex, 1);
    reorderedVisible.splice(toIndex, 0, moved);

    let nextItems: GalleryItem[];
    if (activeCategory === "all") {
      nextItems = reorderedVisible;
    } else {
      let visibleIndex = 0;
      nextItems = items.map((item) =>
        Number(item.category_id) === activeCategory
          ? reorderedVisible[visibleIndex++]
          : item
      );
    }

    const numbered = nextItems.map((item, index) => ({
      ...item,
      order_number: index + 1,
    }));
    setItems(numbered);
    try {
      await reorderGallery(numbered.map(({ id }) => id));
      setMessage("Gallery order saved.");
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to save order");
      setItems(previousItems);
    }
  };

  return (
    <div className="page-layout gallery-admin">
      <div className="page-header">
        <div>
          <h2 className="title">Gallery</h2>
          <p className="gallery-subtitle">Upload media and drag cards to control display order.</p>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <form className="section-card gallery-upload-form" onSubmit={handleUpload}>
        <div className="gallery-uploader-heading">
          <div className="gallery-uploader-icon">
            <i className="fa-solid fa-photo-film"></i>
          </div>
          <div>
            <h3>Add gallery media</h3>
            <p>Upload a project photo or video and add its display details.</p>
          </div>
        </div>

        <div className="gallery-uploader-layout">
          <label
            className={`gallery-dropzone ${media ? "has-media" : ""} ${
              isUploadDragging ? "is-dragging" : ""
            }`}
            htmlFor="gallery-media"
            onDragEnter={(event) => {
              event.preventDefault();
              setIsUploadDragging(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                setIsUploadDragging(false);
              }
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsUploadDragging(false);
              void selectMedia(event.dataTransfer.files?.[0]);
            }}
          >
            <input
              id="gallery-media"
              className="gallery-file-input"
              type="file"
              accept=".heic,.heif,image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,video/mp4,video/webm,video/quicktime"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                void selectMedia(event.target.files?.[0])
              }
            />
            {isConvertingHeic ? (
              <div className="gallery-heic-converting">
                <div className="gallery-conversion-spinner"></div>
                <strong>Converting HEIC to JPEG…</strong>
                <span>This happens in your browser before upload.</span>
              </div>
            ) : media ? (
              <>
                <div className="gallery-selected-preview">
                  {isHeicFile(media) ? (
                    <div className="gallery-heic-placeholder">
                      <i className="fa-solid fa-image"></i>
                      <span>HEIC</span>
                      <small>Preview available after conversion</small>
                    </div>
                  ) : media.type.startsWith("image/") ? (
                    <img src={mediaPreviewUrl} alt="Selected upload preview" />
                  ) : (
                    <video src={mediaPreviewUrl} muted preload="metadata" />
                  )}
                  <div className="gallery-file-type">
                    <i className={`fa-solid ${media.type.startsWith("image/") || isHeicFile(media) ? "fa-image" : "fa-video"}`}></i>
                  </div>
                </div>
                <strong>{media.name}</strong>
                <span>{formatFileSize(media.size)} · Click or drop to replace</span>
              </>
            ) : (
              <>
                <div className="gallery-dropzone-icon">
                  <i className="fa-solid fa-cloud-arrow-up"></i>
                </div>
                <strong>Drop an image or video here</strong>
                <span>or click to browse your computer</span>
                <small>JPG, PNG, WebP, GIF, HEIC, MP4, WebM or MOV · Maximum 200 MB</small>
              </>
            )}
          </label>

          <div className="gallery-upload-details">
            <div className="gallery-form-grid">
              <label>
                <span>Category</span>
                <select
                  value={upload.categoryId}
                  onChange={(event) => setUpload({ ...upload, categoryId: event.target.value })}
                  disabled={Boolean(upload.newCategory)}
                >
                  <option value="">Select category</option>
                  {categories.map((category) => (
                    <option value={category.id} key={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>New category <em>optional</em></span>
                <input
                  value={upload.newCategory}
                  maxLength={120}
                  placeholder="Type to create a category"
                  onChange={(event) => setUpload({ ...upload, newCategory: event.target.value })}
                />
              </label>
              <label className="gallery-caption-field">
                <span>Caption <em>optional</em></span>
                <textarea
                  value={upload.caption}
                  maxLength={500}
                  rows={4}
                  placeholder="Describe this project or media…"
                  onChange={(event) => setUpload({ ...upload, caption: event.target.value })}
                />
                <small>{upload.caption.length}/500 characters</small>
              </label>
              <label>
                <span>Display order <em>optional</em></span>
                <input
                  type="number"
                  min="0"
                  value={upload.orderNumber}
                  placeholder="Add to the end"
                  onChange={(event) => setUpload({ ...upload, orderNumber: event.target.value })}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="gallery-uploader-footer">
          <div>
            <i className="fa-solid fa-wand-magic-sparkles"></i>
            Images are automatically resized and compressed.
          </div>
          <button
            className="btn btn-primary gallery-upload-button"
            type="submit"
            disabled={isSaving || isConvertingHeic || !media}
          >
            <i className="fa-solid fa-cloud-arrow-up"></i>
            {isSaving ? " Uploading…" : " Upload media"}
          </button>
        </div>
      </form>

      <div className="section-card">
        <div className="gallery-list-header">
          <h3>Gallery items</h3>
          <span>{filteredItems.length} of {items.length} items</span>
        </div>
        <div className="gallery-filter-tabs" role="tablist" aria-label="Filter gallery by category">
          <button
            type="button"
            role="tab"
            aria-selected={activeCategory === "all"}
            className={activeCategory === "all" ? "active" : ""}
            onClick={() => setActiveCategory("all")}
          >
            All <span>{items.length}</span>
          </button>
          {filterCategories.map((category) => {
            const itemCount = items.filter(
              (item) => Number(item.category_id) === Number(category.id)
            ).length;
            return (
              <button
                type="button"
                role="tab"
                aria-selected={activeCategory === Number(category.id)}
                className={activeCategory === Number(category.id) ? "active" : ""}
                onClick={() => setActiveCategory(Number(category.id))}
                key={category.id}
              >
                {category.name} <span>{itemCount}</span>
              </button>
            );
          })}
        </div>
        {isLoading ? (
          <p>Loading gallery…</p>
        ) : filteredItems.length === 0 ? (
          <div className="gallery-empty">
            {items.length === 0
              ? "No gallery media has been uploaded yet."
              : "No gallery media exists in this category."}
          </div>
        ) : (
          <div className="gallery-management-grid">
            {filteredItems.map((item) => (
              <article
                className={`gallery-management-card ${
                  draggedId === item.id ? "is-dragging" : ""
                } ${dragOverId === item.id && draggedId !== item.id ? "is-drag-over" : ""}`}
                key={item.id}
                onDragEnter={() => draggedId && setDragOverId(item.id)}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                }}
                onDrop={(event) => dropItem(event, item.id)}
              >
                <div
                  className="gallery-drag-handle"
                  title="Drag to reorder"
                  draggable
                  onDragStart={(event) => startDragging(event, item.id)}
                  onDragEnd={finishDragging}
                >
                  <i className="fa-solid fa-grip-vertical"></i>
                  <span>Order {item.order_number}</span>
                </div>
                <div className="gallery-media-preview">
                  {item.media_type === "image" ? (
                    <img src={`${BASE_URL.replace(/\/$/, "")}${item.file_path}`} alt={item.caption || item.original_filename} />
                  ) : (
                    <video controls preload="metadata">
                      <source src={`${BASE_URL.replace(/\/$/, "")}${item.file_path}`} type={item.mime_type} />
                    </video>
                  )}
                </div>
                <div className="gallery-card-summary">
                  <div className="gallery-card-category">{item.category_name}</div>
                  <p>{item.caption || "No caption"}</p>
                  <div className="gallery-card-meta">
                    <span><i className="fa-solid fa-arrow-down-1-9"></i> {item.order_number}</span>
                    <span><i className="fa-regular fa-calendar"></i> {new Date(item.upload_date.replace(" ", "T")).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="gallery-card-actions">
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => openEditModal(item)}>
                    <i className="fa-solid fa-pen"></i> Edit details
                  </button>
                  <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeItem(item)}>
                    <i className="fa-regular fa-trash-can"></i>
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {editingItem && (
        <div
          className="gallery-edit-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeEditModal();
          }}
        >
          <div className="gallery-edit-modal" role="dialog" aria-modal="true" aria-labelledby="gallery-edit-title">
            <div className="gallery-edit-modal-header">
              <div>
                <span>Edit gallery item</span>
                <h3 id="gallery-edit-title">{editingItem.original_filename}</h3>
              </div>
              <button type="button" onClick={closeEditModal} aria-label="Close modal" disabled={isSaving}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            {error && <div className="alert alert-danger gallery-modal-alert">{error}</div>}

            <div className="gallery-edit-modal-body">
              <div className="gallery-edit-preview">
                {editingItem.media_type === "image" ? (
                  <img
                    src={`${BASE_URL.replace(/\/$/, "")}${editingItem.file_path}`}
                    alt={editingItem.caption || editingItem.original_filename}
                  />
                ) : (
                  <video controls preload="metadata">
                    <source
                      src={`${BASE_URL.replace(/\/$/, "")}${editingItem.file_path}`}
                      type={editingItem.mime_type}
                    />
                  </video>
                )}
              </div>

              <div className="gallery-edit-fields">
                <label>
                  <span>Category</span>
                  <select
                    value={editDraft.categoryId}
                    onChange={(event) => setEditDraft({ ...editDraft, categoryId: event.target.value })}
                  >
                    {categories.map((category) => (
                      <option value={category.id} key={category.id}>{category.name}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Caption</span>
                  <textarea
                    rows={5}
                    maxLength={500}
                    value={editDraft.caption}
                    placeholder="Add a caption…"
                    onChange={(event) => setEditDraft({ ...editDraft, caption: event.target.value })}
                  />
                  <small>{editDraft.caption.length}/500 characters</small>
                </label>
                <label>
                  <span>Order number</span>
                  <input
                    type="number"
                    min="0"
                    value={editDraft.orderNumber}
                    onChange={(event) => setEditDraft({ ...editDraft, orderNumber: event.target.value })}
                  />
                </label>
                <div className="gallery-edit-upload-date">
                  <span>Upload date</span>
                  <strong>{new Date(editingItem.upload_date.replace(" ", "T")).toLocaleString()}</strong>
                </div>
              </div>
            </div>

            <div className="gallery-edit-modal-footer">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => removeItem(editingItem)}
                disabled={isSaving}
              >
                <i className="fa-regular fa-trash-can"></i> Delete item
              </button>
              <div>
                <button type="button" className="btn btn-light" onClick={closeEditModal} disabled={isSaving}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={saveItem} disabled={isSaving}>
                  {isSaving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
